/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ErrorCode } from '../modules/order-details';
import { ensureAuthenticated } from '../utilities/is-authenticated';
import { trackedAction } from '../utilities/track-execution-time';

export class GetOrderDetailsInput implements Msdyn365.IActionInput {
    public transactionId: string;
    constructor(ctx: Msdyn365.ICreateActionContext) {
        this.transactionId = ctx.requestContext.query?.transactionId || '';
    }

    public getCacheKey = () => `GetOrderDetails-${this.transactionId}`;
    public getCacheObjectType = () => 'GetOrderDetails';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IOrderDetailScheduleLine {
    OrderNumber: string;
    LineNumber: string;
    ScheduleLineNumber: string;
    ConfirmedQty: string;
    ConfirmedUnit: string;
    ConfirmedDate?: string;
    Language: string;
    ExtensionProperties: any[];
}

export interface IOrderDetailLine {
    LineNumber: string;
    ProductName: string;
    Status: string;
    StateLogistics: string;
    StateInvoice: string;
    RequestedQty: string;
    RequestedDate?: string;
    RequestedUnit: string;
    Currency: string;
    NetValue: string;
    Endcustomer: string;
    ProductNumber: string;
    DeliveredQty: string;
    DeliveryNumber: string;
    DeliveryItem: string;
    InvoiceNumber: string;
    InvoiceItem: string;
    DeliveryDate: string;
    ActualArrivalDate: string;
    GoodsMovementDate: string;
    TrackingAvailable: boolean;
    LivetrackingUrl: string;
    PlannedArrivalDate: string;
    EstimatedArrivalDate: string;
    Legs: IOrderDetailLineLegs[];
    CarrierName: string;
    Container: string;
    ContainerType: string;
    ShipFlightNumber: string;
    CustomerProductNumber: string;

    BLnumber: string;

    ScheduleLines: IOrderDetailScheduleLine[];
    ExtensionProperties: [];
}

export interface IOrderDetailLineLegs {
    Id: number;
    LegType: string;
    ModeOfTransportation: string;
    State: string;
    BlNumber: string;
    CarrierName: string;
    Container: string;
    ContainerType: string;
    ShipFlightNumber: string;
    Events: IOrderDetailLineEvents[];
    ExtensionProperties: [];
}

export interface IOrderDetailLineEvents {
    Id: number;
    Name: string;
    Timestamp: string;
    Location: string;
    ExtensionProperties: [];
}

export interface IOrderDetailResponse {
    '@odata.context': string;
    error?: ErrorCode;
    OrderDetail: IOrderDetail;
    Documents: IOrderDetailDocument[];
    ExtensionProperties: [];
}

export interface IOrderDetail {
    OrderNumber: string;
    PoNumber: string;
    Status: string;
    StateLogistics: string;
    StateInvoice: string;
    ConfirmedDate?: string;
    SoldTo: IShipTo;
    ShipTo: ISoldTo;
    Language: string;
    PaymentTerms: string;
    OrderType: string;
    Incoterm1: string;
    Incoterm2: string;
    Recipient: string;
    TotalPrice: string;
    Currency: string;
    TotalWeight: string;
    Unit: string;
    ReorderAllowed: boolean;
    Endcustomers: boolean;
    RecordCreatedDate: string;
    OrderDetailLines: IOrderDetailLine[];
    ExtensionProperties: any[];
    HeaderTexts?: HeaderTexts[];
    SalesOrganisationText?: string;
}
export interface IOrderDetailDocument {
    Date: string;
    DocumentId: string;
    ExtensionProperties: any[]; // TODO: change type
    FileSize: string;
    Language: string;
    Name: string;
    OrderNumber: string;
    Reference: string;
    Type: string;
}

export interface ISoldTo {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    LocalAddress: ILocalAddress;
}

export interface IShipTo {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate?: string;
    LocalAddress: ILocalAddress;
    ExtensionProperties?: [];
}

export interface ILocalAddress {
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    ExtensionProperties: [];
}

interface HeaderTexts {
    TextId: string;
    TextValue: string;
    ExtensionProperties: [];
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetOrderDetailsInput(args);
};

async function action(orderDetailsInput: GetOrderDetailsInput, ctx: Msdyn365.IActionContext): Promise<IOrderDetailResponse> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IOrderDetailResponse>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetOrderDetail(language='${ctx.requestContext.locale}',orderNumber='${orderDetailsInput.transactionId}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            },
            timeout: 30000
        }
    );
    if (response.status === 200 && response.data) {
        return { ...response.data };
    }
    return { ...({} as IOrderDetailResponse), error: { errorCode: response.status, errorText: response.statusText } };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IOrderDetailResponse>>((input, ctx) => trackedAction(action, input, ctx, 'GetOrderDetail')),
    id: 'GetOrderDetails',
    input: createInput
});
